import { useMemo } from 'react';
import { useLoaderData } from 'react-router';
import type { SerializeFrom } from '~/types/serialize-from';

import {
    DetailAwardsList,
    type DetailAward,
} from '~/ui-rocco/pages/detail-shared/DetailAwardsList';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import {
    ABOUT_SECTION_CAP_CLASS,
    ABOUT_SECTION_CLASS,
    ABOUT_SECTION_HEADING_CLASS,
} from './about-section-heading';

type AboutAward = SerializeFrom<typeof loader>['awards'][number];

/**
 * A practice's award as the shared list takes it. AwardedEntities also
 * carries the professional itself, so what the award was for is the live
 * Project or Product among them (mirrors the legacy info-page loader).
 */
const toDetailAward = (award: AboutAward): DetailAward => {
    const entity = award.awardedEntities?.edges?.find(
        (edge) =>
            (edge.node.__typename === 'Project' ||
                edge.node.__typename === 'Product') &&
            'IsLive' in edge.node &&
            edge.node.IsLive === true,
    )?.node;
    const kind =
        entity?.__typename === 'Project' || entity?.__typename === 'Product'
            ? entity.__typename
            : null;

    return {
        id: award.id,
        year: award.year,
        title: award.title,
        institution: award.institution,
        item:
            kind && entity && 'Link' in entity && entity.Link
                ? {
                      kind,
                      title: entity.Title ?? '',
                      link: entity.Link,
                      // AwardedEntities is a union: Article and BuildingGuide
                      // carry no thumbnail.
                      thumbnail:
                          'Thumbnail' in entity
                              ? (entity.Thumbnail?.desktop ?? undefined)
                              : undefined,
                  }
                : undefined,
    };
};

/**
 * Awards, as a section of its own under About the practice — headed in the
 * overview's own voice rather than numbered as a row, and standing open,
 * because a practice's awards are the tab's strongest claim.
 */
export const ProfessionalAboutAwardsSection = () => {
    const { awards } = useLoaderData<typeof loader>();
    const detailAwards = useMemo(() => awards.map(toDetailAward), [awards]);
    if (detailAwards.length === 0) return null;

    return (
        <section className={ABOUT_SECTION_CLASS}>
            <div className={ABOUT_SECTION_CAP_CLASS}>
                <h2 className={ABOUT_SECTION_HEADING_CLASS}>Awards</h2>
                {/* Below md the About tab's 12 between a head and what it
                    names, as under Locations we service. */}
                <div className="max-md:pt-3 md:pt-[3rem]">
                    <DetailAwardsList
                        awards={detailAwards}
                        className="md:pt-[5rem] md:pb-[5rem]"
                    />
                </div>
            </div>
        </section>
    );
};
